import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { 
  Briefcase, 
  Search,
  UserCheck,
  Mail,
  Phone,
  Users,
  Link,
  Loader2
} from 'lucide-react';
import { toast } from 'sonner';
import { API_URL } from '../utils/apiUrl';

const DealerMasterPage = () => {
  const { token } = useAuth();
  const [dealers, setDealers] = useState([]);
  const [rms, setRms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [rmFilter, setRmFilter] = useState('all');

  const fetchDealers = useCallback(async () => {
    try {
      setLoading(true);
      const res = await fetch(`${API_URL}/dealers`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        setDealers(data);
      } else {
        toast.error('Failed to load dealers');
      }
    } catch (err) {
      toast.error('Error loading dealers');
    } finally {
      setLoading(false);
    }
  }, [token]);

  const fetchRms = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/rms?status=Active`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        setRms(data);
      }
    } catch (err) {
      console.error('Error loading RMs:', err);
    }
  }, [token]);

  useEffect(() => {
    fetchDealers();
    fetchRms();
  }, [fetchDealers, fetchRms]);

  const filteredDealers = dealers.filter((d) => {
    const matchesSearch =
      d.dealer_code.toLowerCase().includes(searchTerm.toLowerCase()) ||
      d.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (d.email && d.email.toLowerCase().includes(searchTerm.toLowerCase()));
    const matchesStatus = statusFilter === 'all' || d.status === statusFilter;
    const matchesRm = rmFilter === 'all' || 
      (rmFilter === 'unassigned' && !d.reporting_rm_id) ||
      d.reporting_rm_id === rmFilter;
    return matchesSearch && matchesStatus && matchesRm;
  });

  const stats = {
    total: dealers.length,
    active: dealers.filter((d) => d.status === 'Active').length,
    withRm: dealers.filter((d) => d.reporting_rm_id).length,
    linked: dealers.filter((d) => d.linked_user_id).length,
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-white flex items-center gap-2">
            <Briefcase className="w-7 h-7 text-green-400" />
            Dealer Master
          </h1>
          <p className="text-slate-400 mt-1">Read-only dealer summary. Use Staff Management to add or edit Dealers.</p>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="card-dark">
          <CardContent className="pt-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-green-500/20">
                <Briefcase className="w-5 h-5 text-green-400" />
              </div>
              <div>
                <p className="text-2xl font-bold text-white">{stats.total}</p>
                <p className="text-xs text-slate-400">Total Dealers</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="card-dark">
          <CardContent className="pt-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-blue-500/20">
                <UserCheck className="w-5 h-5 text-blue-400" />
              </div>
              <div>
                <p className="text-2xl font-bold text-white">{stats.active}</p>
                <p className="text-xs text-slate-400">Active</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="card-dark">
          <CardContent className="pt-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-purple-500/20">
                <Users className="w-5 h-5 text-purple-400" />
              </div>
              <div>
                <p className="text-2xl font-bold text-white">{stats.withRm}</p>
                <p className="text-xs text-slate-400">With RM</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="card-dark">
          <CardContent className="pt-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-amber-500/20">
                <Link className="w-5 h-5 text-amber-400" />
              </div>
              <div>
                <p className="text-2xl font-bold text-white">{stats.linked}</p>
                <p className="text-xs text-slate-400">Linked Users</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card className="card-dark">
        <CardContent className="pt-4">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                placeholder="Search by code, name, or email..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 input-vault"
              />
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-full sm:w-40 input-vault">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="Active">Active</SelectItem>
                <SelectItem value="Inactive">Inactive</SelectItem>
              </SelectContent>
            </Select>
            <Select value={rmFilter} onValueChange={setRmFilter}>
              <SelectTrigger className="w-full sm:w-48 input-vault">
                <SelectValue placeholder="RM Filter" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All RMs</SelectItem>
                <SelectItem value="unassigned">Unassigned (Direct)</SelectItem>
                {rms.map((rm) => (
                  <SelectItem key={rm.rm_code} value={rm.rm_code}>
                    {rm.name} ({rm.rm_code})
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Dealer Table - Read Only */}
      <Card className="card-dark">
        <CardHeader>
          <CardTitle className="text-white">Dealer Directory</CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-green-400" />
            </div>
          ) : filteredDealers.length === 0 ? (
            <div className="text-center py-12 text-slate-400">
              <Briefcase className="w-12 h-12 mx-auto mb-4 opacity-50" />
              <p>No dealers found</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="border-slate-700">
                    <TableHead className="text-slate-300">Code</TableHead>
                    <TableHead className="text-slate-300">Name</TableHead>
                    <TableHead className="text-slate-300">Contact</TableHead>
                    <TableHead className="text-slate-300">Reporting RM</TableHead>
                    <TableHead className="text-slate-300">Clients</TableHead>
                    <TableHead className="text-slate-300">Status</TableHead>
                    <TableHead className="text-slate-300">User Link</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredDealers.map((dealer) => (
                    <TableRow key={dealer.dealer_code} className="border-slate-700">
                      <TableCell className="text-white font-mono font-medium">{dealer.dealer_code}</TableCell>
                      <TableCell className="text-white">{dealer.name}</TableCell>
                      <TableCell className="text-slate-300">
                        <div className="space-y-1">
                          {dealer.email && (
                            <div className="flex items-center gap-1 text-sm">
                              <Mail className="w-3 h-3" /> {dealer.email}
                            </div>
                          )}
                          {dealer.mobile && (
                            <div className="flex items-center gap-1 text-sm">
                              <Phone className="w-3 h-3" /> {dealer.mobile}
                            </div>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        {dealer.reporting_rm_id ? (
                          <Badge className="bg-purple-500/20 text-purple-400 border-purple-500/30">
                            {dealer.reporting_rm_name || dealer.reporting_rm_id}
                          </Badge>
                        ) : (
                          <Badge className="bg-slate-500/20 text-slate-400 border-slate-500/30">
                            Direct to Principal
                          </Badge>
                        )}
                      </TableCell>
                      <TableCell className="text-white font-medium">{dealer.client_count || 0}</TableCell>
                      <TableCell>
                        <Badge
                          className={
                            dealer.status === 'Active'
                              ? 'bg-green-500/20 text-green-400 border-green-500/30'
                              : 'bg-red-500/20 text-red-400 border-red-500/30'
                          }
                        >
                          {dealer.status}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        {dealer.linked_user_id ? (
                          <Badge className="bg-blue-500/20 text-blue-400 border-blue-500/30">
                            {dealer.linked_user_email || 'Linked'}
                          </Badge>
                        ) : (
                          <Badge className="bg-amber-500/20 text-amber-400 border-amber-500/30">
                            Not Linked
                          </Badge>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default DealerMasterPage;
